import { useCallback, useMemo, useRef } from 'react';

import { Alert, Loader, ScrollArea, Stack, Text } from '@mantine/core';

import { useAuth } from '@/contexts/AuthContext';
import { useChatMessages, useChatThreads, useSendChatMessage } from '@/hooks/useChat';
import { useConversationAttachments } from '@/hooks/useConversationAttachments';
import { useStickToBottom } from '@/hooks/useStickToBottom';
import { vnextApiClient } from '@/services/vnext-api';
import { ChatThreadKind } from '@/types/chat';
import { Composer } from './Composer';
import { MessageAttachment } from './MessageAttachment';
import { MessageBubble } from './MessageBubble';

interface ChatPanelProps {
  entityType: 'enquiry' | 'report' | 'report_request';
  entityId: string;
  /** Which thread on this entity to render. The drawer wraps two
   * panels in tabs (Client + Internal) for analysts. */
  kind: ChatThreadKind;
  /** Required for np_thread; ignored otherwise. NP threads key the
   * thread by (entity, kind, np). */
  npId?: string;
  /** When true, the composer exposes the internal-note toggle. */
  canSendPrivate?: boolean;
  /** Admin merged view: fold the internal sibling thread's notes into
   * this (client) thread's listing. Pair with `canSendPrivate` so the
   * composer's internal checkbox routes notes onto the sibling. */
  mergeInternal?: boolean;
  /** When true, the composer exposes the attach affordance (paperclip +
   * drag-drop), uploading to this thread. Off by default so existing
   * call sites stay text-only. */
  enableAttachments?: boolean;
  /** Optional text to seed the composer with once (e.g. a canned
   * message). The analyst can edit before sending. */
  seedText?: string;
}

/**
 * Embeddable chat panel — Phase 07 in-platform UX over Freshdesk.
 *
 * Creates the thread for the (entity, kind, np) tuple lazily — on the
 * first send or upload, never on mount, so opening a page writes
 * nothing. The canonical anchor is resolved server-side via
 * ChatService.resolve_thread_anchor — Report-page panels walk up to the
 * parent ReportRequest, etc. — so the SPA just passes its page-local
 * entity context.
 *
 * Subscribes to SSE for live message arrival; auto-scrolls to the
 * bottom on new messages and on initial load.
 *
 * Currently does not auto-call mark_seen — wait for an explicit user
 * gesture (window focus, scroll-to-bottom). Future enhancement.
 */
export function ChatPanel({
  entityType,
  entityId,
  kind,
  npId,
  canSendPrivate = false,
  mergeInternal = false,
  enableAttachments = false,
  seedText,
}: Readonly<ChatPanelProps>) {
  const auth = useAuth();
  const myUserId = auth?.user?.id ?? null;
  const myEmail = auth?.user?.email ?? null;

  const {
    threads,
    isLoading: threadsLoading,
    refetch: refetchThreads,
  } = useChatThreads(entityType, entityId);

  const thread = useMemo(
    () => threads.find((t) => t.kind === kind && (kind !== 'np_thread' || t.npId === npId)) ?? null,
    [threads, kind, npId]
  );
  const threadId = thread?.id;

  // The thread is created the first time the user actually writes or
  // uploads. One in-flight creation is shared so a burst of uploads
  // can't create twice.
  const creatingRef = useRef<Promise<string> | null>(null);
  const ensureThread = useCallback(async (): Promise<string> => {
    if (threadId) {
      return threadId;
    }
    creatingRef.current ??= vnextApiClient
      .createChatThread({ entityType, entityId, kind, npId })
      .then(async (created) => {
        await refetchThreads();
        return created.id;
      })
      .finally(() => {
        creatingRef.current = null;
      });
    return creatingRef.current;
  }, [threadId, entityType, entityId, kind, npId, refetchThreads]);

  const { messages, isLoading } = useChatMessages(threadId, mergeInternal);
  const { send, isSending, error: sendError } = useSendChatMessage(threadId);
  const uploadContext = useMemo(() => ({ entityType, entityId }), [entityType, entityId]);
  const { upload } = useConversationAttachments(
    enableAttachments ? threadId : undefined,
    uploadContext
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  useStickToBottom(scrollRef, messages.length, threadId);

  return (
    <Stack gap="sm" h="100%" style={{ minHeight: 0, minWidth: 0, flex: 1 }}>
      {isLoading && !messages.length && <Loader size="sm" />}
      {sendError && (
        <Alert color="red" title="Failed to send">
          {sendError.message}
        </Alert>
      )}
      <ScrollArea viewportRef={scrollRef} style={{ flex: 1, minHeight: 0 }} type="auto">
        {messages.length === 0 && !isLoading && (
          <Text c="dimmed" size="sm">
            No messages yet — say hello.
          </Text>
        )}
        {messages.map((m, i) => {
          // Mine = my user-id matches (covers the optimistic outbound
          // row before any round-trip) OR my email matches the sender
          // (covers the round-tripped inbound copy persisted by the
          // Freshdesk webhook, which has no user-id).
          const isMine = Boolean(
            (myUserId && m.senderUserId === myUserId) ||
            (myEmail && m.senderEmail?.toLowerCase() === myEmail.toLowerCase())
          );
          // Group consecutive same-sender messages so a run reads as
          // one utterance and we only render the sender label once.
          const prev = messages[i - 1];
          const groupedWithPrev = Boolean(
            prev?.senderEmail === m.senderEmail &&
            prev?.isPrivate === m.isPrivate &&
            // Same minute: enough to feel like a continuation; new
            // minute restarts a label/timestamp header.
            prev?.createdAt &&
            m.createdAt &&
            new Date(prev.createdAt).getMinutes() === new Date(m.createdAt).getMinutes() &&
            new Date(prev.createdAt).getHours() === new Date(m.createdAt).getHours()
          );
          const attachments = m.attachments ?? [];
          return (
            <Stack key={m.id} gap={2}>
              <MessageBubble message={m} isMine={isMine} groupedWithPrev={groupedWithPrev} />
              {attachments.length > 0 && (
                <Stack
                  gap={4}
                  px={4}
                  align={isMine ? 'flex-end' : 'flex-start'}
                  style={{ maxWidth: '78%', alignSelf: isMine ? 'flex-end' : 'flex-start' }}
                >
                  {attachments.map((att) => (
                    <MessageAttachment key={att.id} attachment={att} compact />
                  ))}
                </Stack>
              )}
            </Stack>
          );
        })}
      </ScrollArea>
      <Composer
        canSendPrivate={canSendPrivate}
        onSend={async (body) => {
          await send(body, await ensureThread());
        }}
        isSending={isSending}
        disabled={threadsLoading}
        onUploadAttachment={
          enableAttachments ? async (file) => upload(file, await ensureThread()) : undefined
        }
        seedText={seedText}
      />
    </Stack>
  );
}
